import { el, clear } from '../../core/dom.js';
import { skeleton } from '../../components/skeleton.js';
import { toast } from '../../components/toast.js';
import { assembleCatalogue } from '../connectors/catalogue.js';
import { openConnectorDialog } from '../connectors/dialog.js';
import { loadConnectorBundle } from './actions.js';
import { connectorRungs, DEPTH_WEIGHTS } from './model.js';

/**
 * Step 3 — connectors: analysis depth + Behaviour analytics + payoff groups.
 * In-flow sources (GA4 / BigQuery / Firebase / Mixpanel) call onConnect(source).
 * @param {object} state
 * @param {{
 *   me: object,
 *   productName: string,
 *   onSkip: () => void,
 *   onConnect: (source: string) => void,
 * }} opts
 */
export function renderConnectorsStep(state, opts) {
  const root = el('div', { class: 'np-conn' });
  const head = el('div');
  const body = el('div', { class: 'np-conn-body' }, skeleton(4));
  const foot = el('div', { class: 'np-actions' },
    el('button', { type: 'button', class: 'np-btn', onClick: opts.onSkip }, 'Skip for now'));
  root.append(head, body, foot);

  const local = {
    dead: false,
    items: [],
    depth: { analysis_depth: 0, depth_breakdown: {} },
    depthError: '',
    fatal: '',
  };

  root.addEventListener('bonnie:unmount', () => { local.dead = true; });
  load();
  return root;

  async function load() {
    const bundle = await loadConnectorBundle(state.productId);
    if (local.dead) return;
    apply(bundle);
    paint();
  }

  function apply(bundle) {
    const integrations = bundle.integrations || {};
    const providers = bundle.providers || {};
    if (integrations.error) {
      local.fatal = integrations.error;
      return;
    }
    if (providers.error) {
      local.fatal = providers.error;
      return;
    }
    local.fatal = '';
    const rows = integrations.integrations || [];
    const servers = bundle.mcp && !bundle.mcp.error ? (bundle.mcp.servers || []) : [];
    local.items = assembleCatalogue(providers.providers || [], rows, servers).map((item) => ({
      ...item,
      productId: state.productId,
    }));
    local.depth = bundle.depth && !bundle.depth.error
      ? bundle.depth
      : { analysis_depth: 0, depth_breakdown: {} };
    local.depthError = bundle.depth && bundle.depth.error ? bundle.depth.error : '';
  }

  function paint() {
    clear(head);
    clear(body);
    const name = opts.productName || state.name || 'this product';
    head.append(el('header', { class: 'np-conn-head' },
      el('div', { class: 'np-conn-copy' },
        el('div', { class: 'np-head-row' },
          el('h2', { class: 'np-title' },
            elFragment('Connect the tools ', el('em', {}, 'that matter.'))),
          el('span', { class: 'np-step-tag' }, 'Step 3 of 3')),
        el('p', { class: 'np-lead' },
          'Start with analytics. Everything else builds depth around the same product.')),
      depthMeter(local.depth, local.depthError, load)));

    if (local.fatal) {
      body.append(el('div', { class: 'np-glass np-why' },
        el('p', {}, local.fatal),
        el('button', { type: 'button', class: 'np-btn', onClick: load }, 'Try again')));
      return;
    }

    const rungs = connectorRungs(local.items);
    const analytics = rungs.find((rung) => rung.key === 'analytics');
    const payoff = rungs.filter((rung) => !rung.required);

    if (analytics) body.append(analyticsCard(analytics));
    body.append(el('p', { class: 'np-pay-label lbl rv' }, 'Then, in this order of payoff'));
    body.append(el('div', { class: 'np-pay' },
      ...payoff.map((group, index) => payoffCard(group, index))));
  }

  function depthMeter(depth, failed, onRetry) {
    if (failed) {
      return el('section', { class: 'np-depth rv', 'aria-label': 'Analysis depth' },
        el('p', { class: 'np-sub' }, failed),
        el('button', { type: 'button', class: 'np-btn', onClick: onRetry }, 'Try again'));
    }
    const score = Number(depth && depth.analysis_depth) || 0;
    const width = Math.max(score, score === 0 ? 2 : 0);
    return el('section', { class: 'np-depth rv', 'aria-label': 'Analysis depth' },
      el('div', { class: 'np-depth-top' },
        el('span', { class: 'np-lbl' }, 'Analysis depth'),
        el('span', { class: 'np-depth-score' }, `${score}%`)),
      el('div', { class: 'np-depth-track', 'aria-hidden': 'true' },
        el('div', { class: 'np-depth-fill', style: `width:${width}%` })),
      el('span', { class: 'np-depth-hint' },
        score > 0
          ? `Behaviour analytics contributes +${DEPTH_WEIGHTS.analytics}% when live`
          : 'Connect any source to unlock chat'));
  }

  function analyticsCard(rung) {
    return el('section', {
      class: 'np-rung is-required rv np-rv-1',
      'aria-label': rung.title,
    },
    el('div', { class: 'np-rung-banner' },
      el('span', { class: 'np-lbl np-rung-step' }, 'Step 1'),
      el('span', { class: 'np-rung-display' }, rung.title),
      el('span', { class: 'np-rung-pill' }, rung.gain)),
    el('p', { class: 'np-rung-blurb' }, rung.blurb),
    el('div', { class: 'np-rung-actions' },
      ...rung.items.map((item) => connectChip(item, true))));
  }

  function payoffCard(group, index) {
    return el('section', {
      class: 'np-pay-card np-glass rv',
      style: `animation-delay:${(0.22 + index * 0.06).toFixed(2)}s`,
      'aria-label': group.title,
    },
    el('div', { class: 'np-pay-top' },
      el('span', { class: 'np-pay-title' }, group.title),
      el('span', { class: 'np-pay-gain' }, group.gain)),
    el('p', { class: 'np-rung-blurb' }, group.blurb),
    el('div', { class: 'np-rung-actions' },
      ...group.items.map((item) => connectChip(item, false))));
  }

  function connectChip(item, analytics) {
    const label = item.label || item.title || item.name || 'Connect';
    const dim = !!(item.soon || item.unsupported);
    const live = !!item.connected;

    if (dim) {
      return el('button', {
        type: 'button',
        class: 'np-cn is-dim',
        disabled: true,
        title: 'Not available yet',
      }, mono(item), label);
    }

    if (live) {
      return el('span', { class: 'np-cn is-live' }, mono(item), 'Live');
    }

    const admin = !!(opts.me && opts.me.role === 'admin');
    if (!admin) {
      return el('button', {
        type: 'button',
        class: 'np-cn is-dim',
        disabled: true,
      }, mono(item), 'Ask an admin');
    }

    const primary = analytics && item.primary;
    return el('button', {
      type: 'button',
      class: primary ? 'np-cn is-pri' : 'np-cn',
      onClick: () => open(item),
    }, mono(item), label);
  }

  function mono(item) {
    const letter = (item.letter || (item.title || item.label || '?').charAt(0)).toUpperCase();
    return el('span', { class: 'np-cn-ico', 'aria-hidden': 'true' }, letter);
  }

  function open(item) {
    if (!state.productId) {
      toast('Save the product first', 'error');
      return;
    }
    if (item.inFlow && item.source && typeof opts.onConnect === 'function') {
      opts.onConnect(item.source);
      return;
    }
    openConnectorDialog(item, 'connect', { me: opts.me, productId: state.productId }, () => load());
  }
}

function elFragment(...nodes) {
  return nodes;
}
